import { getOperationDisplayName, getOperationActionName } from '../constants/modelOperations'

/**
 * Format a date string or number into a localized string
 */
export const formatDate = (date: string | number) => {
    return new Date(date).toLocaleString(undefined, {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    })
}

export const stripLeadingArticle = (name: string): string => {
    return name.replace(/^(a |an |the )/i, '').trim()
}

/**
 * Calculate the duration between two dates in a human-readable format
 */
export const formatElapsedFromDates = (startDate: string | number, endDate: string | number) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diff = end.getTime() - start.getTime()

    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

    if (days > 0) {
        return `${days}d ${hours}h`
    } else if (hours > 0) {
        return `${hours}h ${minutes}m`
    } else {
        return `${minutes}m`
    }
}

// Helper function to format elapsed time
export const formatElapsedFromSeconds = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60

    if (hours > 0) {
        return `${hours}h ${minutes}m ${remainingSeconds}s`
    } else if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`
    }
    return `${remainingSeconds}s`
}

export const formatCompactNumber = (number: number): string => {
    const formatter = Intl.NumberFormat('en', { notation: 'compact' })
    return formatter.format(number)
}

export const formatOperationType = (type: string) => {
    return getOperationDisplayName(type)
}

export const formatOperationTypeAction = (type: string) => {
    return getOperationActionName(type)
}

export const formatStageDisplay = (stage?: string) => {
    switch (stage) {
        case 'generating':
            return `Generating data, `
        case 'correlation':
            return `Finding correlations, `
        case 'ranking_features':
            return `Ranking features, `
        case 'multicollinearity':
            return `Checking for similar components, `
        case 'quick_train':
            return `Testing, `
        case 'canceling':
            return `Canceling, `
        case 'canceled':
            return `Canceled, `
        case 'training':
            return `Training, `
        case 'ensembles':
            return `Building ensembles, `
        case 'tune_models':
            return `Tuning model, `
        case 'backtesting':
            return `Backtesting model, `
        default:
            return `Starting, `
    }
}

export const formatFirebaseError = (errorMsg: string) => {
    // Handle Firebase auth errors with better user-friendly messages
    // Check both SDK format (auth/email-already-in-use) and REST API format (EMAIL_EXISTS)
    if (errorMsg.includes('auth/email-already-in-use') || errorMsg.includes('EMAIL_EXISTS')) {
        return {
            error: {
                message: 'An account with this email already exists.',
                status: 'EMAIL_IN_USE',
            },
        }
    }

    if (errorMsg.includes('auth/user-not-found')) {
        return {
            error: {
                message: 'No account found with this email address. Please check your email or create a new account.',
                status: 'USER_NOT_FOUND',
            },
        }
    }

    if (errorMsg.includes('auth/wrong-password')) {
        return {
            error: {
                message: 'Incorrect password. Please try again.',
                status: 'WRONG_PASSWORD',
            },
        }
    }

    if (errorMsg.includes('auth/weak-password')) {
        return {
            error: {
                message: 'Password is too weak. Please choose a stronger password (at least 6 characters).',
                status: 'WEAK_PASSWORD',
            },
        }
    }

    if (errorMsg.includes('auth/invalid-email')) {
        return {
            error: {
                message: 'Please enter a valid email address.',
                status: 'INVALID_EMAIL',
            },
        }
    }

    if (errorMsg.includes('auth/too-many-requests')) {
        return {
            error: {
                message: 'Too many failed attempts. Please try again later.',
                status: 'TOO_MANY_REQUESTS',
            },
        }
    }

    if (errorMsg.includes('auth/network-request-failed')) {
        return {
            error: {
                message: 'Network error. Please check your connection and try again.',
                status: 'NETWORK_ERROR',
            },
        }
    }

    if (errorMsg.includes('auth/popup-closed-by-user')) {
        return {
            error: {
                message: 'Sign-in was cancelled. Please try again.',
                status: 'POPUP_CLOSED',
            },
        }
    }

    if (errorMsg.includes('auth/popup-blocked')) {
        return {
            error: {
                message: 'Sign-in popup was blocked. Please allow popups for this site and try again.',
                status: 'POPUP_BLOCKED',
            },
        }
    }

    // Try to parse JSON if it exists
    const jsonStart = errorMsg.indexOf('{')
    const jsonEnd = errorMsg.lastIndexOf('}')

    if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
        const jsonString = errorMsg.substring(jsonStart, jsonEnd + 1)
        try {
            return JSON.parse(jsonString)
        } catch (error) {
            console.error('Error parsing JSON:', error)
        }
    }

    // Fallback for unknown errors
    return {
        error: {
            message: 'An unexpected error occurred. Please try again.',
            status: 'UNKNOWN',
        },
    }
}

const KNOWN_STAGES = ['debug', 'dev', 'dev-ai', 'staging', 'prod']

export const getEnvironmentBanner = (user?: { roles: string[] } | null) => {
    const stage = import.meta.env.VITE_APP_API_STAGE || 'prod'
    const apiOverride = localStorage.getItem('apiEndpointOverride')

    // Only show banner for developer users
    if (!user?.roles?.includes('developer')) {
        return null
    }

    // Check for API override
    if (apiOverride === 'staging' || apiOverride === 'prod') {
        const overrideText = apiOverride === 'prod' ? 'PROD API' : 'STAGING API'
        const baseText = KNOWN_STAGES.includes(stage) ? `${stage}` : `${stage} dev`
        return {
            text: `${baseText} → ${overrideText}`,
            color: apiOverride === 'prod' ? 'error.main' : 'warning.main',
        }
    }

    // For known stages, show the stage name with appropriate color
    if (KNOWN_STAGES.includes(stage)) {
        const color = stage === 'prod' ? 'error.main' : stage === 'staging' ? 'orange' : 'info.main' // dev, debug, etc.
        return {
            text: `${stage} environment`,
            color,
        }
    }

    // For developer stages (like 'jason'), show '{devname} dev environment' with blue color
    return {
        text: `${stage} dev environment`,
        color: 'info.main',
    }
}

// export const buildApiUrl = (api: string, protocolType?: string, path?: string, isShared = false) => {
//     // Check for API override in localStorage (direct check to avoid circular dependency)
//     const apiOverride = localStorage.getItem('apiEndpointOverride')

//     let stage = import.meta.env.VITE_APP_API_STAGE || 'prod'
//     let domain = import.meta.env.VITE_APP_API_DOMAIN || 'moddy.ai'

//     // Apply override if set
//     if (apiOverride === 'staging' || apiOverride === 'prod') {
//         stage = apiOverride
//         domain = 'moddy.ai'
//     }

//     // Build URL based on stage
//     let url: string
//     const protocol = protocolType || 'https'

//     if (stage === 'prod') {
//         // Production: identity-api.moddy.ai
//         url = `${protocol}://${api}.${domain}${path || ''}`
//     } else if (stage === 'staging') {
//         // Staging: identity-api.staging.moddy.ai
//         url = `${protocol}://${api}.staging.${domain}${path || ''}`
//     } else if (KNOWN_STAGES.includes(stage)) {
//         // Known stages (dev, debug): identity-api.dev.moddy.ai
//         url = `${protocol}://${api}.${stage}.${domain}${path || ''}`
//     } else if (isShared) {
//         // Shared resources use base domain
//         url = `${protocol}://${api}.${domain}${path || ''}`
//     } else {
//         // Developer stages: identity-api-todd.dev.moddy.ai
//         url = `${protocol}://${api}-${stage}.${domain}${path || ''}`
//     }

//     return url
// }

export const buildApiUrl = (api: string, protocolType?: string, path?: string, isShared = false) => {
    const stageOverride = localStorage.getItem('apiEndpointOverride')
    const domainOverride =
        stageOverride === 'prod' ? 'moddy.ai' : stageOverride === 'staging' ? 'staging.moddy.ai' : undefined
    const stage = stageOverride || import.meta.env.VITE_APP_API_STAGE || 'prod'
    const domain = domainOverride || import.meta.env.VITE_APP_API_DOMAIN || 'moddy.ai'
    const subdomain = KNOWN_STAGES.includes(stage) || isShared ? api : `${api}-${stage}`
    const protocol = protocolType || 'https'
    const url = `${protocol}://${subdomain}.${domain}${path || ''}`
    return url
}

export const buildWebSiteUrl = (path?: string) => {
    return `https://www.moddy.ai${path || ''}`
}
